import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Course } from '../types';
import { coursesAPI } from '../services/api';

const Home: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    coursesAPI.getCourses()
      .then(data => setCourses(data.slice(0, 3)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white">

      {/* Hero */}
      <div className="bg-gradient-to-br from-medical-900 via-medical-800 to-medical-700 text-white">
        <div className="max-w-5xl mx-auto px-6 py-24 lg:py-32">
          <p className="text-medical-300 text-sm font-semibold uppercase tracking-widest mb-5">
            Paris, France · Founded 2022
          </p>
          <h1 className="font-display text-4xl lg:text-6xl font-bold leading-tight mb-6">
            Medical imaging training<br />from people in the field.
          </h1>
          <p className="text-medical-100 text-lg lg:text-xl max-w-2xl leading-relaxed mb-10">
            We've installed, serviced, and supported CT and MRI systems across the Middle East and Africa.
            ScanMedX turns that field experience into structured training for technologists, engineers, and radiographers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/courses"
              className="bg-white text-medical-900 hover:bg-medical-50 px-8 py-3.5 rounded-xl font-semibold transition-colors inline-flex items-center justify-center"
            >
              Browse courses →
            </Link>
            <a
              href="mailto:contact@scanmedx.com"
              className="border border-white/30 hover:bg-white/10 text-white px-8 py-3.5 rounded-xl font-medium transition-colors inline-flex items-center justify-center"
            >
              Get in touch
            </a>
          </div>
        </div>
      </div>

      {/* Courses */}
      <div className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-12">
            <p className="text-medical-600 font-semibold text-sm uppercase tracking-wide mb-3">Courses</p>
            <h2 className="font-display text-3xl font-bold text-gray-900">Available now</h2>
            <p className="text-gray-500 mt-2">
              Structured training modules covering specific scanner families and workflows.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-2 border-medical-200 border-t-medical-600" />
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <p className="text-lg font-medium text-gray-600 mb-2">No courses published yet.</p>
              <p className="text-sm">Check back soon.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {courses.map(course => (
                <div key={course.id} className="border border-gray-200 rounded-2xl p-6 hover:shadow-md transition-shadow">
                  <span className="inline-block bg-medical-50 text-medical-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                    CT Training
                  </span>
                  <h3 className="font-display text-lg font-bold text-gray-900 mb-2 leading-snug">
                    {course.title}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-3">
                    {course.description}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-400 mb-5">
                    <span>{course.instructor?.full_name || 'ScanMedX'}</span>
                    <span>{course.lessons?.length || 0} lessons</span>
                  </div>
                  <Link
                    to={`/courses/${course.id}`}
                    className="block text-center bg-medical-600 hover:bg-medical-700 text-white py-2.5 px-4 rounded-xl font-medium text-sm transition-colors"
                  >
                    View course
                  </Link>
                </div>
              ))}
            </div>
          )}

          {!loading && courses.length > 0 && (
            <div className="mt-10">
              <Link
                to="/courses"
                className="inline-flex items-center text-medical-600 font-medium hover:text-medical-700 transition-colors"
              >
                See all courses →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Blog & Support */}
      <div className="bg-gray-50 py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="mb-12">
            <p className="text-medical-600 font-semibold text-sm uppercase tracking-wide mb-3">Also on this platform</p>
            <h2 className="font-display text-3xl font-bold text-gray-900">Blog &amp; Support</h2>
            <p className="text-gray-500 mt-2 max-w-xl">
              Practical articles on scanner protocols and equipment workflows, plus direct access to the people who built these courses.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-2xl p-7">
              <h3 className="font-bold text-gray-900 text-lg mb-2">Blog</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Short articles on CT and MRI scanner settings, protocol decisions, maintenance notes,
                and observations from the field. No filler.
              </p>
              <span className="text-xs text-gray-400 mt-4 block">Coming soon</span>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-7">
              <h3 className="font-bold text-gray-900 text-lg mb-2">Support</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Questions about a course, a scanner workflow, or a specific protocol?
                We aim to respond within a working day.
              </p>
              <a
                href="mailto:contact@scanmedx.com"
                className="text-medical-600 hover:text-medical-700 text-sm font-medium mt-4 block transition-colors"
              >
                contact@scanmedx.com →
              </a>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default Home;
